import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
// Uppercase alias so the JSX-only usage passes this repo's no-unused-vars
// config (varsIgnorePattern ^[A-Z_]), which doesn't count JSX references.
import { motion as Motion } from 'framer-motion';
import {
    Plus, Search, X, Eye, Pencil, Trash2, FlaskConical, ScanLine,
    PackageOpen,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';
import {
    fetchCatalog, createCatalogItem, updateCatalogItem,
    toggleCatalogItem, deleteCatalogItem,
} from '../../redux/masterCatalogSlice';
import {
    MASTER_CATALOG_KINDS, isMasterCatalogKind, emptyCatalogForm, catalogSubtitle,
} from '../../data/masterCatalogKinds';
import useCanManage from '../../hooks/useCanManage';

// ONE page for the two world-level catalogs (lab_test / imaging_procedure —
// no medicine master since 10 Oct 2026) — the registry in data/masterCatalogKinds.js decides the
// fields, exactly as Backend/config/masterCatalogs.js decides them for the
// API. Super admin curates WHAT AN ITEM IS; prices deliberately don't exist
// here (they are each hospital's own business).

const KIND_ICONS = { lab_test: FlaskConical, imaging_procedure: ScanLine };

const MasterCatalog = () => {
    const { kind } = useParams();
    const { t } = useTranslation('mastercatalog');
    const dispatch = useDispatch();
    // Write-side controls hide without the manage key — the API refuses
    // them anyway (hooks/useCanManage.js).
    const canManage = useCanManage('masters.manage');
    const kindState = useSelector((s) => s.masterCatalog.byKind[kind]) || { items: [], count: 0, loading: true };
    const actionLoading = useSelector((s) => s.masterCatalog.actionLoading);

    const [activeTab, setActiveTab] = useState('active');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [showFormModal, setShowFormModal] = useState(false);
    const [viewingItem, setViewingItem] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});

    const def = isMasterCatalogKind(kind) ? MASTER_CATALOG_KINDS[kind] : null;
    const KindIcon = KIND_ICONS[kind] || PackageOpen;

    useEffect(() => {
        const timer = setTimeout(() => { setDebouncedSearch(searchQuery); setCurrentPage(1); }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Reset the list state when hopping between the three catalog nav entries.
    // debouncedSearch must reset too — otherwise the first fetch of the new
    // kind still filters by the previous kind's search term.
    useEffect(() => { setSearchQuery(''); setDebouncedSearch(''); setActiveTab('active'); setCurrentPage(1); }, [kind]);

    const refresh = useMemo(() => (page = currentPage) => {
        dispatch(fetchCatalog({ kind, page, limit: itemsPerPage, search: debouncedSearch, status: activeTab }));
    }, [dispatch, kind, currentPage, itemsPerPage, debouncedSearch, activeTab]);

    useEffect(() => { if (def) refresh(); }, [def, refresh]);

    // Deleting/deactivating the sole item on the last page leaves an empty
    // page with the pagination hidden — step back to the last page that has
    // rows instead of stranding the user on a fake empty state.
    const itemCount = kindState.items?.length ?? 0;
    useEffect(() => {
        if (!kindState.loading && itemCount === 0 && kindState.count > 0 && currentPage > 1) {
            setCurrentPage(Math.max(1, Math.ceil(kindState.count / itemsPerPage)));
        }
    }, [kindState.loading, kindState.count, itemCount, currentPage, itemsPerPage]);

    if (!def) return <Navigate to="/" replace />;

    const items = kindState.items || [];

    const handlePageChange = (page, newLimit) => {
        if (newLimit && newLimit !== itemsPerPage) { setItemsPerPage(newLimit); setCurrentPage(1); }
        else setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const openCreate = () => {
        setEditingItem(null);
        setFormData(emptyCatalogForm(kind));
        setErrors({});
        setShowFormModal(true);
    };

    const openEdit = (item) => {
        setEditingItem(item);
        const base = emptyCatalogForm(kind);
        for (const f of def.fields) {
            if (item[f.key] !== undefined && item[f.key] !== null) base[f.key] = item[f.key];
        }
        setFormData(base);
        setErrors({});
        setShowFormModal(true);
    };

    const handleField = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
    };

    const validate = () => {
        const next = {};
        for (const f of def.fields) {
            if (f.required && String(formData[f.key] ?? '').trim() === '') {
                next[f.key] = t('mastercatalog.field_required', { field: f.label, defaultValue: `${f.label} is required` });
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        const data = {};
        for (const f of def.fields) {
            const v = formData[f.key];
            if (f.type === 'checkbox') data[f.key] = Boolean(v);
            else if (String(v ?? '').trim() !== '') data[f.key] = f.type === 'number' ? Number(v) : String(v).trim();
            // An explicit clear on edit. Numbers clear as null — '' is a
            // Postgres type error for DECIMAL/INTEGER columns.
            else if (editingItem) data[f.key] = f.type === 'number' ? null : '';
        }
        try {
            if (editingItem) {
                await dispatch(updateCatalogItem({ kind, id: editingItem[def.idField], data })).unwrap();
                toast.success(t('mastercatalog.updated', { label: def.label, defaultValue: `${def.label} updated` }));
            } else {
                await dispatch(createCatalogItem({ kind, data })).unwrap();
                toast.success(t('mastercatalog.created', { label: def.label, defaultValue: `${def.label} added to the master` }));
                setCurrentPage(1);
            }
            setShowFormModal(false);
            refresh(editingItem ? currentPage : 1);
        } catch (err) {
            toast.warn(err?.error || t('mastercatalog.save_failed', 'Could not save the item'));
        }
    };

    const handleToggle = async (item) => {
        try {
            await dispatch(toggleCatalogItem({ kind, id: item[def.idField] })).unwrap();
            refresh();
        } catch (err) {
            toast.warn(err?.error || t('mastercatalog.toggle_failed', 'Could not change the status'));
        }
    };

    const handleDelete = async (item) => {
        const result = await Swal.fire({
            title: t('mastercatalog.delete_title', 'Remove from master?'),
            text: t('mastercatalog.delete_text', {
                name: item.name,
                defaultValue: `"${item.name}" will disappear from the master list. Hospitals that already picked it keep their copy.`,
            }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('mastercatalog.yes_delete', 'Yes, remove'),
            cancelButtonText: t('mastercatalog.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;
        try {
            await dispatch(deleteCatalogItem({ kind, id: item[def.idField] })).unwrap();
            toast.success(t('mastercatalog.deleted', 'Removed from the master'));
            refresh();
        } catch (err) {
            toast.warn(err?.error || t('mastercatalog.delete_failed', 'Could not remove the item'));
        }
    };

    const renderInput = (f) => {
        const invalid = Boolean(errors[f.key]);
        const base = `w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${invalid ? 'border-red-300 bg-red-50' : 'border-gray-300'}`;
        if (f.type === 'select') {
            return (
                <select
                    value={formData[f.key] ?? ''}
                    onChange={(e) => handleField(f.key, e.target.value)}
                    className={base}
                    data-testid={`master-catalog-field-${f.key}`}
                >
                    <option value="">{t('mastercatalog.not_set', '— not set —')}</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        }
        if (f.type === 'textarea') {
            return (
                <textarea
                    rows={3}
                    value={formData[f.key] ?? ''}
                    onChange={(e) => handleField(f.key, e.target.value)}
                    maxLength={500}
                    className={`${base} resize-none`}
                    placeholder={f.placeholder || ''}
                    data-testid={`master-catalog-field-${f.key}`}
                />
            );
        }
        if (f.type === 'checkbox') {
            return (
                <label className="inline-flex items-center gap-2 cursor-pointer select-none py-2">
                    <input
                        type="checkbox"
                        checked={Boolean(formData[f.key])}
                        onChange={(e) => handleField(f.key, e.target.checked)}
                        className="w-4 h-4 accent-[#4ABDBD]"
                        data-testid={`master-catalog-field-${f.key}`}
                    />
                    <span className="text-sm text-gray-600">{t('mastercatalog.yes', 'Yes')}</span>
                </label>
            );
        }
        return (
            <input
                type={f.type === 'number' ? 'number' : 'text'}
                value={formData[f.key] ?? ''}
                min={f.min} max={f.max} step={f.step}
                onChange={(e) => handleField(f.key, e.target.value)}
                maxLength={120}
                className={base}
                placeholder={f.placeholder || ''}
                data-testid={`master-catalog-field-${f.key}`}
            />
        );
    };

    return (
        <div className="w-full" data-testid="master-catalog-container">
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3 ml-2">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center shadow-md">
                            <KindIcon size={22} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="master-catalog-title">
                                {t(`mastercatalog.title_${kind}`, def.labelPlural)}
                            </h1>
                            <p className="text-xs text-gray-400">
                                {t('mastercatalog.subtitle', 'World catalog — hospitals pick from this list. No prices here; tariffs are each hospital\'s own.')}
                            </p>
                        </div>
                    </div>
                    {canManage && (
                    <button
                        onClick={openCreate}
                        className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
                        data-testid="master-catalog-add-button"
                    >
                        <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                        {t('mastercatalog.add', { label: def.label, defaultValue: `Add ${def.label}` })}
                    </button>
                    )}
                </div>

                {/* Tabs + search */}
                <div className="mb-5 flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                        {['active', 'inactive'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                                className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                                data-testid={`master-catalog-tab-${tab}`}
                            >
                                {activeTab === tab && (
                                    <Motion.div
                                        layoutId="masterCatalogStatusTab"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20 capitalize">{t(`mastercatalog.${tab}`, tab)}</span>
                            </button>
                        ))}
                    </div>
                    <div className="relative group w-full sm:w-1/3">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={20} />
                        <input
                            type="text"
                            placeholder={t('mastercatalog.search', def.searchHint)}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 60))}
                            className="w-full pl-11 pr-4 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400"
                            data-testid="master-catalog-search-input"
                        />
                    </div>
                </div>

                {/* Grid */}
                {kindState.loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4" data-testid="master-catalog-skeleton">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
                                <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
                                <div className="h-3 bg-gray-100 rounded w-1/2 mb-4"></div>
                                <div className="h-8 bg-gray-100 rounded"></div>
                            </div>
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-16" data-testid="master-catalog-empty">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <KindIcon size={44} className="text-gray-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">
                            {debouncedSearch
                                ? t('mastercatalog.none_found', 'Nothing matches your search')
                                : t('mastercatalog.none_yet', { label: def.labelPlural.toLowerCase(), defaultValue: `No ${def.labelPlural.toLowerCase()} in the master yet` })}
                        </h3>
                        {canManage && !debouncedSearch && activeTab === 'active' && (
                            <button
                                onClick={openCreate}
                                className="mt-2 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg font-semibold"
                            >
                                <Plus size={20} />
                                {t('mastercatalog.add_first', { label: def.label, defaultValue: `Add the first ${def.label.toLowerCase()}` })}
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4" data-testid="master-catalog-grid">
                            {items.map((item) => {
                                const id = item[def.idField];
                                const isInactive = item.status === 'inactive';
                                return (
                                    <div
                                        key={id}
                                        className={`group relative bg-white border rounded-lg p-4 overflow-hidden transition-all duration-300 ${isInactive ? 'border-gray-200 opacity-75' : 'border-gray-200 hover:border-[#4ABDBD] hover:shadow-lg hover:-translate-y-0.5'}`}
                                        data-testid={`master-catalog-card-${id}`}
                                    >
                                        {!isInactive && (
                                            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                                        )}
                                        {/* status toggle */}
                                        <div className="absolute top-3 right-3 z-20">
                                            <div
                                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${isInactive ? 'bg-gray-300' : 'bg-[#4ABDBD]'} ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                onClick={() => canManage && !actionLoading && handleToggle(item)}
                                                data-testid={`master-catalog-toggle-${id}`}
                                            >
                                                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform duration-300 shadow-sm ${isInactive ? 'translate-x-1' : 'translate-x-[18px]'}`}></span>
                                            </div>
                                        </div>

                                        <div className="mb-3 pr-12">
                                            <h3 className={`text-base font-bold line-clamp-1 mb-1 transition-colors ${isInactive ? 'text-gray-400' : 'text-gray-800 group-hover:text-[#4ABDBD]'}`}>
                                                {item.name}
                                            </h3>
                                            <p className={`text-xs line-clamp-1 mb-1 ${isInactive ? 'text-gray-400' : 'text-[#2D8A8A]'}`}>
                                                {catalogSubtitle(kind, item) || ' '}
                                            </p>
                                        </div>

                                        <div className="flex items-center justify-center gap-2 pt-3 border-t border-gray-100">
                                            <button
                                                onClick={() => setViewingItem(item)}
                                                className="text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105"
                                                title={t('mastercatalog.view', 'View')}
                                                data-testid={`master-catalog-view-${id}`}
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            {canManage && (
                                            <button
                                                onClick={() => openEdit(item)}
                                                className="text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105"
                                                title={t('mastercatalog.edit', 'Edit')}
                                                data-testid={`master-catalog-edit-${id}`}
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            )}
                                            {canManage && (
                                            <button
                                                onClick={() => handleDelete(item)}
                                                disabled={actionLoading}
                                                className="text-red-500 hover:text-white p-2 rounded-lg bg-red-50 hover:bg-red-500 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 disabled:opacity-50"
                                                title={t('mastercatalog.delete', 'Remove')}
                                                data-testid={`master-catalog-delete-${id}`}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {kindState.count > 0 && (
                            <Pagination
                                totalItems={kindState.count}
                                currentPage={kindState.page || currentPage}
                                itemsPerPage={kindState.limit || itemsPerPage}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </>
                )}

                {/* Create / Edit modal */}
                {showFormModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="master-catalog-form-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                                <h2 className="text-xl font-bold text-gray-800">
                                    {editingItem
                                        ? t('mastercatalog.edit_title', { label: def.label, defaultValue: `Edit ${def.label}` })
                                        : t('mastercatalog.add_title', { label: def.label, defaultValue: `Add ${def.label} to the master` })}
                                </h2>
                                <button onClick={() => setShowFormModal(false)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-testid="master-catalog-form-close">
                                    <X size={24} />
                                </button>
                            </div>
                            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {def.fields.map((f) => (
                                    <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            {t(`mastercatalog.field_${f.key}`, f.label)}
                                            {f.required && <span className="text-red-500 ml-0.5">*</span>}
                                        </label>
                                        {renderInput(f)}
                                        {errors[f.key] && <p className="mt-1 text-sm text-red-500">{errors[f.key]}</p>}
                                    </div>
                                ))}
                            </div>
                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    onClick={() => setShowFormModal(false)}
                                    disabled={actionLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50"
                                >
                                    {t('mastercatalog.cancel', 'Cancel')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={actionLoading}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                                    data-testid="master-catalog-form-save"
                                >
                                    {actionLoading && <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>}
                                    {editingItem ? t('mastercatalog.update', 'Update') : t('mastercatalog.create', 'Add to master')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* View modal */}
                {viewingItem && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="master-catalog-view-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800 line-clamp-1">{viewingItem.name}</h2>
                                <button onClick={() => setViewingItem(null)} className="p-1 hover:bg-gray-100 rounded transition-colors">
                                    <X size={24} />
                                </button>
                            </div>
                            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                {def.fields.map((f) => {
                                    const v = viewingItem[f.key];
                                    if (v === undefined || v === null || String(v).trim() === '') return null;
                                    return (
                                        <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                                            <p className="text-xs font-medium text-gray-400 mb-0.5">{t(`mastercatalog.field_${f.key}`, f.label)}</p>
                                            <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                                {f.type === 'checkbox' ? (v ? t('mastercatalog.yes', 'Yes') : t('mastercatalog.no', 'No')) : String(v)}
                                            </p>
                                        </div>
                                    );
                                })}
                                <div className="sm:col-span-2 pt-2 border-t border-gray-100 flex items-center justify-between">
                                    <span />
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${viewingItem.status === 'active' ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-gray-200 text-gray-500 border border-gray-300'}`}>
                                        {viewingItem.status === 'active' ? t('mastercatalog.active', 'Active') : t('mastercatalog.inactive', 'Inactive')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MasterCatalog;
